import React, { useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Trash2, Pen, Loader2, Type, Check, FileSignature } from 'lucide-react';

interface SignatureCaptureProps {
  onSignature: (signatureData: string) => void;
  onSave?: (signatureData: string) => Promise<void>;
  signature?: string;
  disabled?: boolean;
  loading?: boolean;
  onNameChange?: (name: string) => void;
}

export const SignatureCapture: React.FC<SignatureCaptureProps> = ({
  onSignature,
  onSave,
  signature,
  disabled = false,
  loading = false,
  onNameChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const typedCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!signature);
  const [error, setError] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isSaving, setIsSaving] = useState(false);

  const getCoordinates = useCallback((event: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect) return null;

    // Handle touch events
    if ('touches' in event) {
      const touch = event.touches[0] || event.changedTouches[0];
      return {
        x: (touch.clientX - rect.left) * (canvas.width / rect.width),
        y: (touch.clientY - rect.top) * (canvas.height / rect.height)
      };
    }
    
    // Handle mouse events
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height)
    };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (disabled || loading) return;
    e.preventDefault();
    setError(null);
    setIsDrawing(true);
    
    const coords = getCoordinates(e);
    const canvas = canvasRef.current;
    if (canvas && coords) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000000'; // Use explicit black color
      }
    }
  }, [disabled, loading, getCoordinates]);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || disabled || loading) return;
    e.preventDefault();
    
    const coords = getCoordinates(e);
    const canvas = canvasRef.current;
    if (canvas && coords) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
      }
    }
  }, [isDrawing, disabled, loading, getCoordinates]);

  const stopDrawing = useCallback((e?: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (disabled || loading) return;
    if (e) e.preventDefault();
    
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      // Check if canvas has content
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((channel, index) => 
          index % 4 === 3 && channel !== 0 // Check alpha channel for any non-transparent pixels
        );
        
        if (hasContent) {
          setHasSignature(true);
          try {
            const signatureData = canvas.toDataURL('image/png', 0.8);
            onSignature(signatureData);
          } catch (error) {
            setError('Failed to capture signature');
            console.error('Signature capture error:', error);
          }
        } else {
          setHasSignature(false);
          onSignature('');
        }
      }
    }
  }, [disabled, loading, onSignature]);

  const generateTypedSignature = useCallback(() => {
    if (!typedSignature.trim()) {
      setHasSignature(false);
      onSignature('');
      return;
    }

    const canvas = typedCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Set white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Set signature font
        ctx.font = '36px Dancing Script, cursive';
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Draw the typed signature
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
        
        try {
          const signatureData = canvas.toDataURL('image/png', 0.8);
          setHasSignature(true);
          setSaveStatus('saved');
          onSignature(signatureData);
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
          setError('Failed to generate typed signature');
          setSaveStatus('error');
          console.error('Typed signature error:', error);
        }
      }
    }
  }, [typedSignature, onSignature]);

const handleTypedSignatureChange = useCallback((value: string) => {
  setTypedSignature(value);
  setError(null);
  if (onNameChange) {
    onNameChange(value);
  }
}, [onNameChange]);

  const saveSignature = useCallback(async () => {
    if (!hasSignature || !onSave) return;

    const currentSignature = signatureMode === 'draw' 
      ? canvasRef.current?.toDataURL('image/png', 0.8)
      : typedCanvasRef.current?.toDataURL('image/png', 0.8);

    if (!currentSignature) {
      setError('No signature to save');
      return;
    }

    setIsSaving(true);
    setSaveStatus('saving');
    setError(null);

    try {
      await onSave(currentSignature);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      setError('Failed to save signature');
      setSaveStatus('error');
      console.error('Save signature error:', error);
    } finally {
      setIsSaving(false);
    }
  }, [hasSignature, onSave, signatureMode]);

  const clearSignature = useCallback(() => {
    if (disabled || loading) return;
    
    if (signatureMode === 'draw') {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
    } else {
      setTypedSignature('');
      const canvas = typedCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
    }
    
    setHasSignature(false);
    setError(null);
    setSaveStatus('idle');
    onSignature('');
  }, [disabled, loading, onSignature, signatureMode]);

  // Generate typed signature when text changes
  React.useEffect(() => {
    if (signatureMode === 'type' && typedSignature.trim()) {
      const timer = setTimeout(generateTypedSignature, 300);
      return () => clearTimeout(timer);
    }
  }, [signatureMode, typedSignature, generateTypedSignature]);

  // Initialize canvas and load existing signature
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Set white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Load existing signature if provided
        if (signature) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setHasSignature(true);
          };
          img.onerror = () => {
            setError('Failed to load existing signature');
          };
          img.src = signature;
        }
      }
    }
  }, [signature]);

  return (
    <Card className="glass-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Pen className="h-4 w-4" />
          Digital Signature
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <Tabs value={signatureMode} onValueChange={(value) => setSignatureMode(value as 'draw' | 'type')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="draw" className="flex items-center gap-2">
              <Pen className="h-4 w-4" />
              Draw
            </TabsTrigger>
            <TabsTrigger value="type" className="flex items-center gap-2">
              <Type className="h-4 w-4" />
              Type
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="draw" className="space-y-4 mt-4">
            <div className="border-2 border-dashed border-muted rounded-lg p-2 bg-background">
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className={`w-full border rounded bg-background ${
                  disabled || loading ? 'opacity-50 pointer-events-none' : 'cursor-crosshair touch-none'
                }`}
                style={{ touchAction: 'none' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Draw your signature in the box above using your mouse or finger.
            </p>
          </TabsContent>
          
          <TabsContent value="type" className="space-y-4 mt-4">
            <div className="space-y-3">
              <Input
                type="text"
                placeholder="Enter your full name"
                value={typedSignature}
                onChange={(e) => handleTypedSignatureChange(e.target.value)}
                disabled={disabled || loading}
                className="text-lg"
              />
              
              {typedSignature && (
                <div className="border-2 border-dashed border-muted rounded-lg p-2 bg-background">
                  <canvas
                    ref={typedCanvasRef}
                    width={400}
                    height={150}
                    className="w-full border rounded bg-background"
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Type your full name above to generate a signature automatically.
            </p>
          </TabsContent>
        </Tabs>
        
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
            {error}
          </div>
        )}
        
        {saveStatus === 'saved' && (
          <div className="text-sm text-emerald-600 bg-emerald-50 p-2 rounded flex items-center gap-2">
            <Check className="h-4 w-4" />
            Signature saved successfully
          </div>
        )}
        
        <div className="flex gap-2">
          {onSave && (
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={saveSignature}
              disabled={disabled || loading || !hasSignature || isSaving}
              className="flex-1"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSignature className="h-4 w-4 mr-2" />
              )}
              Save Signature
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearSignature}
            disabled={disabled || loading || !hasSignature}
            className={onSave ? "flex-1" : "flex-1"}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Clear
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground">
          Please sign above to confirm completion of this checklist. {hasSignature && '✓ Signature captured'}
        </p>
      </CardContent>
    </Card>
  );
};